/**
 * Confirming and firing the armed strategy — ROADMAP 5.7.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS THE CONFIRMATION, NOT THE CHOOSER.
 *
 * Choosing a strategy moved to `StrategyDropdown`, which hangs off the box in
 * the chain header. What is left here is what a trader reads before
 * committing: the legs that will actually be sent, the price they will
 * actually fetch, the shape at expiry, and the checklist.
 *
 * THERE IS NO TYPED CONFIRMATION, ON ANY MODE. The owner removed it: a LIVE
 * basket places on the button, exactly as PAPER does, and "Skip for this
 * entry" places too. What remains is the checklist, which is a pause rather
 * than a gate — see `placeOrder.ts` for the same decision on the ladder, and
 * what it costs.
 *
 * Every number is computed by the same pure functions the order path uses —
 * the preview and the order cannot drift because there is only one
 * implementation.
 *
 * THE PREVIEW IS BUILT FROM THE DIRECTED STRATEGY.
 *
 * The flip is applied BEFORE resolving, not after. Resolving the saved legs
 * and then placing them under a SELL button sends the long structure under a
 * short button — the direction has to reach `resolveLegs` and `netPremiumOf`
 * or the preview prices one basket and the order sends another.
 *
 * WHETHER TO FLIP IS DERIVED, NOT PASSED IN. The caller sends the INTENT the
 * trader pressed; `shouldInvert` compares it with the side the structure is
 * already written in. A Short Straddle fired with SELL is placed exactly as
 * saved — the earlier version inverted it and bought two ATM legs under a
 * red button.
 *
 * The payoff glyph is DRAWN FROM THE LEGS rather than stored beside them. A
 * saved picture can disagree with the position: edit an offset and it
 * silently lies about what will be traded.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { SlidersHorizontal, X } from 'lucide-react'
import {
  expiryCode,
  isTradingWindow,
  lotsForMargin,
  netPremiumOf,
  resolveExpiry,
  shouldInvert,
  type BrokerConnectionDto,
  type ExpiryKind,
  type MarginEstimate,
  type ResolvedLeg,
  type TradeIntent,
  type UnresolvedLeg,
  type ProtectionSpec,
} from '@apna/shared'
import { notifyFail } from '@/components/Toast'
import { api } from '@/lib/api'
import { tickSocket } from '@/feed/tickSocket'
import { useTickSubscription } from '@/feed/useTick'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import type { StrategyDto } from '@/types/strategy'
import { PayoffChart } from '@/components/payoff/PayoffChart'
import { PreTradeChecklist } from './PreTradeChecklist'
import { directedName, structureLabel } from './strategyLabel'
import { payoffLegsOf } from './previewPayoff'
import { LegEditor, type LegEdit } from './LegEditor'
import { PayoffExplain } from './PayoffExplain'
import { StrategyName } from './StrategyName'
import { tickStore } from '@/feed/tickStore'
import { marginForLegs } from './marginFor'
import type { BasketSeed } from '@/screens/BasketBuilder/basketSeed'
import { compactInr } from './quantity'
import { deltaReaderOf } from './useChainGreeks'
import { rowsOf, useChainsByExpiry } from './useChainsByExpiry'
import type { ChainGreeks } from './useChainGreeks'
import {
  useStrategyPreviews,
  type PreviewChainRow,
  type StrategyPreview,
} from '@/hooks/useStrategyPreviews'

/** What the header's size control is set to. */
export type PickerSizing =
  | { readonly mode: 'LOTS'; readonly lots: number }
  | {
      readonly mode: 'RUPEES'
      readonly budget: number
      readonly armed: boolean
    }

export interface StrategyPickerProps {
  open: boolean
  onClose: () => void
  /** The armed strategy, exactly as saved. Direction is applied here. */
  strategy: StrategyDto | null
  rows: readonly PreviewChainRow[]
  spot: number
  step: number
  lotSize: number
  /**
   * HOW THE HEADER IS SIZING, not just how many lots.
   *
   * In LOTS mode this is the stepper's value. In RUPEES mode the basket
   * sizes ITSELF, because only it knows what the basket costs: a short
   * structure blocks initial margin, which on a NIFTY lot is six figures
   * against a few thousand rupees of premium, so dividing a budget by
   * premium would place roughly fifteen times the intended size.
   */
  sizing: PickerSizing
  /**
   * The chain's solved volatilities and forward, for the margin scan. The
   * SAME pass the greeks column uses — see `marginFor.ts`.
   */
  greeks: ChainGreeks
  /** True when the chain's expiry is today; adds the 2% short-option ELM. */
  isExpiryDay: boolean
  underlying: string
  /** YYYY-MM-DD, already resolved by the expiry selector. */
  expiry: string | null
  /**
   * Every expiry this underlying lists — 9.43.
   *
   * ─────────────────────────────────────────────────────────────────────
   * HANDED DOWN SO THE LEGS CAN CHOOSE, NOT SO THE TICKET CAN
   *
   * 9.41 put one expiry selector in this header, which was the wrong
   * shape: a strategy is a set of legs and a leg owns its expiry, so a
   * calendar spread — the same strike on two dates — could be saved in
   * the builder and never fired. The selector is now on every row of the
   * leg table, and a header naming one date would contradict a basket
   * spanning two.
   *
   * This list is the CATALOGUE's, not a selection. Nothing here changes
   * the chain the Terminal is rendering.
   * ─────────────────────────────────────────────────────────────────────
   */
  expiries?: readonly string[]
  /** Which expiry kinds the chain answers to — see `useStrategyPreviews`. */
  chainKinds?: readonly ExpiryKind[]
  connection: BrokerConnectionDto | null
  onPlaced: () => void
  /**
   * A strategy edited from inside the modal — today, its checklist. The
   * caller holds the armed copy, so it has to hear about the change or the
   * next entry shows the old list.
   */
  onStrategyChanged?: (strategy: StrategyDto) => void
  /**
   * Which way the trader asked to trade the structure — the header's
   * SELL / BUY pair. The saved strategy is untouched; any flip applies to
   * THIS placement only.
   */
  intent: TradeIntent
  /**
   * Hand these legs to the basket builder instead of placing them — 9.69.
   *
   * The ticket has already resolved every criterion into a contract and
   * applied the direction; what crosses here is that ANSWER, so the trader
   * arrives at a basket holding exactly the strikes they were just looking
   * at. Omitted, the button is not drawn.
   */
  onAdjust?: (seed: BasketSeed) => void
}

/** The distinct reasons behind a failed resolve, in the order legs failed. */
function distinctWhy(
  unresolved: readonly UnresolvedLeg[] | undefined,
): string[] {
  return [...new Set((unresolved ?? []).map((u) => u.why))]
}

export function StrategyPicker({
  open,
  onClose,
  strategy,
  rows,
  spot,
  step,
  lotSize,
  sizing,
  greeks,
  isExpiryDay,
  underlying,
  expiry,
  expiries,
  chainKinds,
  connection,
  onPlaced,
  onStrategyChanged,
  intent,
  onAdjust,
}: StrategyPickerProps) {
  const [placing, setPlacing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /*
   * The checklist reports two different things, and the order path needs
   * both: whether the place button may unlock (ticked OR deliberately
   * skipped), and whether the checks were actually COMPLETED, which is what
   * gets written on the deployment.
   */
  const [checklistReady, setChecklistReady] = useState(false)
  const [checklistDone, setChecklistDone] = useState(false)
  /** The list as shown, with what was ticked — sent with the entry (9.8). */
  const [checklist, setChecklist] = useState<
    readonly { rule: string; ticked: boolean }[]
  >([])
  const onChecklist = useCallback(
    (
      ready: boolean,
      done: boolean,
      result: readonly { rule: string; ticked: boolean }[],
    ) => {
      setChecklistReady(ready)
      setChecklistDone(done)
      setChecklist(result)
    },
    [],
  )

  /*
   * Inversion is applied to a COPY so the preview shows — and the order
   * sends — the legs that will actually be placed. The trader's saved
   * strategy is never mutated; that is 5.19's job and needs an explicit save.
   */
  const selected: StrategyDto | null = useMemo(() => {
    if (!strategy) return null
    if (!shouldInvert(intent, strategy.legs)) return strategy
    return {
      ...strategy,
      legs: strategy.legs.map((l) => ({
        ...l,
        side: l.side === 'BUY' ? ('SELL' as const) : ('BUY' as const),
      })),
    }
  }, [strategy, intent])

  // A refusal belongs to the placement that produced it.
  useEffect(() => {
    setError(null)
  }, [open, strategy, intent])

  // One strategy, but the same cadence rules: recomputed off the tick store
  // on a slow timer, never per tick (§4.7), and only while open.
  const forPreview = useMemo(() => (selected ? [selected] : []), [selected])

  /*
   * SIZING BY MONEY NEEDS THE BASKET PRICED FIRST, which needs a size —
   * so the basket is resolved once at ONE LOT and the answer scaled.
   *
   * Scaling is exact, not an approximation: every leg's quantity is
   * `lots × ratio × lotSize`, so the scenario losses, the exposure and the
   * premium are all linear in lots. One probe therefore answers "what does
   * a lot of this cost" for any budget, and nothing iterates.
   */
  /*
   * 5.23. What a `CLOSEST_DELTA` leg picks its strike with. Derived from the
   * pass the chain column already ran — never solved again; see
   * `deltaReaderOf`.
   */
  const deltaOf = useMemo(() => deltaReaderOf(greeks), [greeks])

  /*
   * ── WHICH EXPIRY EACH LEG TRADES — ROADMAP 9.43 ───────────────────────
   *
   * The DEFAULT comes from the template: a leg that names an expiry kind
   * gets that kind resolved against the listed dates, and a leg that names
   * none takes the chain's. This is the first thing that makes a calendar
   * strategy placeable — the builder has stored per-leg kinds since 9.37
   * and nothing downstream had ever read them into a date.
   *
   * `resolveExpiry` rather than any arithmetic here: which date is "next
   * month" is knowable only from what the exchange has listed, and the one
   * definition of that lives in `expiryKind.ts`.
   */
  const defaultExpiries = useMemo(() => {
    const out: string[] = []
    for (const leg of selected?.legs ?? []) {
      const kind = leg.expiryKind
      if (kind == null) {
        out.push(expiry ?? '')
        continue
      }
      const pick = resolveExpiry(kind, expiries ?? [])
      out.push(pick.ok ? pick.date : (expiry ?? ''))
    }
    return out
  }, [selected, expiry, expiries])

  /*
   * ── THIS PLACEMENT'S EDITS ────────────────────────────────────────────
   *
   * A template's strike is an ATM offset; the strike a trader wants is the
   * one with the open interest, or the one past the level they are
   * watching. Both are editable here and NOWHERE ELSE: the saved strategy
   * is untouched, because an edit made seconds before an order is almost
   * never one to keep.
   *
   * Keyed by leg index and cleared whenever the modal reopens or the armed
   * strategy changes, for the same reason the ticks and the typed word are.
   */
  const [edits, setEdits] = useState<ReadonlyMap<number, LegEdit>>(new Map())
  useEffect(() => setEdits(new Map()), [open, strategy, intent])

  /** This leg's expiry: the trader's override, else the template's default. */
  const expiryOf = useCallback(
    (index: number): string =>
      edits.get(index)?.expiry ?? defaultExpiries[index] ?? expiry ?? '',
    [edits, defaultExpiries, expiry],
  )

  /*
   * Every expiry some leg needs, so their ladders are fetched. The chain's
   * own is included and costs nothing — `prefetchChain` dedupes against the
   * cache the Terminal already filled.
   */
  const neededExpiries = useMemo(() => {
    const out = new Set<string>()
    if (expiry) out.add(expiry)
    for (let i = 0; i < (selected?.legs.length ?? 0); i += 1) {
      const e = expiryOf(i)
      if (e) out.add(e)
    }
    return [...out]
  }, [expiry, selected, expiryOf])

  const chains = useChainsByExpiry(underlying, neededExpiries, open)

  /**
   * The ladders a CALENDAR leg needs, keyed by the kind it names — 9.43.
   *
   * Only the kinds some leg actually names, so a single-expiry strategy
   * costs nothing: the map is empty and `resolveLegs` sees no
   * `chainByExpiry` at all, exactly as before.
   */
  const rowsByKind = useMemo(() => {
    const out = new Map<ExpiryKind, readonly PreviewChainRow[]>()
    for (const leg of selected?.legs ?? []) {
      const kind = leg.expiryKind
      if (kind == null || out.has(kind)) continue
      const pick = resolveExpiry(kind, expiries ?? [])
      if (!pick.ok) continue
      const ladder = pick.date === expiry ? rows : rowsOf(chains, pick.date)
      if (ladder.length > 0) out.set(kind, ladder)
    }
    return out
  }, [selected, expiries, expiry, rows, chains])

  /**
   * The ladder for one expiry.
   *
   * The CHAIN'S OWN expiry is served from the `rows` prop rather than from
   * the cache, because that is the array the preview resolved against and
   * the one the table is rendering. Two ladders for one date, differing by
   * a refresh, is how a strike picker comes to offer a contract the
   * resolver did not see.
   */
  const rowsFor = useCallback(
    (e: string): readonly PreviewChainRow[] =>
      e === expiry ? rows : rowsOf(chains, e),
    [expiry, rows, chains],
  )

  const probe = useStrategyPreviews({
    strategies: forPreview,
    rows,
    spot,
    step,
    lotSize,
    lots: 1,
    deltaOf,
    ...(chainKinds ? { chainKinds } : {}),
    rowsByKind,
    enabled: open && sizing.mode === 'RUPEES',
  })
  const perLot = selected ? probe.get(selected.id) : undefined

  const marginPerLot: MarginEstimate | null = useMemo(() => {
    if (sizing.mode !== 'RUPEES' || !perLot?.legs) return null
    return marginForLegs(
      perLot.legs.map((l) => ({
        token: l.token,
        side: l.side,
        optionType: l.optionType,
        strike: l.strike,
        lots: l.lots,
        price: (l.side === 'SELL' ? l.bid : l.ask) ?? l.ltp,
      })),
      { greeks, spot, lotSize, isExpiryDay },
    )
  }, [sizing.mode, perLot, greeks, spot, lotSize, isExpiryDay])

  /** What will actually be placed, after the budget is divided. */
  const lots =
    sizing.mode === 'LOTS'
      ? sizing.lots
      : sizing.armed && marginPerLot?.ok
        ? lotsForMargin(sizing.budget, marginPerLot.margin.total)
        : 0

  const previews = useStrategyPreviews({
    strategies: forPreview,
    rows,
    spot,
    step,
    lotSize,
    lots: Math.max(1, lots),
    deltaOf,
    ...(chainKinds ? { chainKinds } : {}),
    rowsByKind,
    enabled: open,
  })
  const preview = selected ? previews.get(selected.id) : undefined

  /**
   * The legs that will actually be sent, and why they could not be built.
   *
   * An edited strike or expiry is re-pointed at the CONTRACT that strike
   * lists on that date — token and prices read from the same store the
   * chain renders from — so everything downstream (payoff, premium,
   * margin, the order) is computed from one set of legs rather than from
   * the resolver's and a patch.
   *
   * IT FAILS AS A WHOLE, like the resolver it continues. A leg that cannot
   * be pointed is not dropped: the payload reads `selected.legs[i]` for
   * each leg's ratio, role and criteria, so a short array would silently
   * send one leg's size with the next leg's rule. Four legs of a six-leg
   * basket is a different position, and three of them would be naked.
   */
  const { legs, editProblem } = useMemo((): {
    legs: readonly ResolvedLeg[] | null
    editProblem: string | null
  } => {
    const base = preview?.legs
    if (!base) return { legs: null, editProblem: null }
    if (edits.size === 0) return { legs: base, editProblem: null }

    const out: ResolvedLeg[] = []
    for (const [i, leg] of base.entries()) {
      const edit = edits.get(i)
      if (!edit) {
        out.push(leg)
        continue
      }
      const ladder = rowsFor(edit.expiry)
      const row = ladder.find((r) => r.strike === edit.strike)
      const token = leg.optionType === 'CE' ? row?.ceToken : row?.peToken
      if (!token) {
        return {
          legs: null,
          editProblem:
            ladder.length === 0
              ? `Loading the ${expiryCode(edit.expiry) ?? edit.expiry} chain…`
              : `Leg ${i + 1}: ${edit.strike} ${leg.optionType} is not listed on ${
                  expiryCode(edit.expiry) ?? edit.expiry
                }.`,
        }
      }
      const quote = tickStore.snapshot(token)
      out.push({
        ...leg,
        token,
        strike: edit.strike,
        lots: edit.lots,
        ltp: quote?.ltp ?? 0,
        ...(quote?.bid === undefined ? {} : { bid: quote.bid }),
        ...(quote?.ask === undefined ? {} : { ask: quote.ask }),
      })
    }
    return { legs: out, editProblem: null }
  }, [preview, edits, rowsFor])

  /*
   * ── SUBSCRIBE WHAT THIS BASKET WILL TRADE — ROADMAP 9.40 ───────────────
   *
   * 9.40 freed the RESOLVER from the chain's render window, so a leg on a
   * strike nobody has scrolled to now resolves. It still has no price,
   * because only the window was ever subscribed — and a basket that
   * resolves but says "not priceable" is half an answer.
   *
   * So the picker asks for its own legs while it is open. Refcounted like
   * every other subscription, so closing it gives them back; scoped to the
   * open modal, so a picker nobody is looking at spends nothing.
   *
   * Small by construction — at most one basket's worth, against a chain's
   * hundreds — and the legs are exactly the contracts an order is about to
   * be sent for, which is the strongest claim on the budget there is.
   *
   * It reads the EDITED legs, not the resolver's — 9.43. Moving a leg to
   * another expiry re-points it at a contract on a ladder the chain is not
   * rendering, so subscribing what the resolver found would leave the one
   * leg the trader just changed as the only one without a price.
   */
  useTickSubscription(
    useMemo(
      () =>
        open
          ? [
              ...new Set(
                [...(legs ?? []), ...(preview?.legs ?? [])]
                  .map((l) => l.token)
                  .filter((t) => t > 0),
              ),
            ]
          : [],
      [open, legs, preview],
    ),
  )

  /** Re-priced from the legs above, so an edit moves every number at once. */
  const netPremium = useMemo(() => {
    if (!legs || !(lotSize > 0)) return null
    const priced = netPremiumOf(legs, lotSize)
    return priced.ok ? priced.netPremium : null
  }, [legs, lotSize])

  /** The margin the basket as sized will actually block. */
  const margin: MarginEstimate | null = useMemo(() => {
    if (!legs || lots < 1) return null
    return marginForLegs(
      legs.map((l) => ({
        token: l.token,
        side: l.side,
        optionType: l.optionType,
        strike: l.strike,
        lots: l.lots,
        price: (l.side === 'SELL' ? l.bid : l.ask) ?? l.ltp,
      })),
      { greeks, spot, lotSize, isExpiryDay },
    )
  }, [legs, lots, greeks, spot, lotSize, isExpiryDay])

  /** Why ₹ mode cannot size this basket, when it cannot. */
  const sizingProblem: string | null =
    sizing.mode !== 'RUPEES'
      ? null
      : !sizing.armed
        ? 'Set an amount and press OK in the header.'
        : marginPerLot === null
          ? 'Waiting for prices…'
          : !marginPerLot.ok
            ? marginPerLot.why
            : lots < 1
              ? `One lot needs about ₹${compactInr(marginPerLot.margin.total)} — more than the budget.`
              : null

  const ready =
    Boolean(selected) &&
    Boolean(connection) &&
    Boolean(legs?.length) &&
    editProblem === null &&
    lots >= 1 &&
    sizingProblem === null &&
    checklistReady

  /** Rename the saved strategy from here, and tell the caller. */
  const rename = useCallback(
    (name: string) => {
      if (!strategy) return
      void (async () => {
        try {
          const { strategy: updated } = await api.patch<{
            strategy: StrategyDto
          }>(`/strategies/${strategy.id}`, { name })
          onStrategyChanged?.(updated)
        } catch (e: unknown) {
          setError(
            e instanceof Error ? e.message : 'The name could not be saved.',
          )
        }
      })()
    },
    [strategy, onStrategyChanged],
  )

  /**
   * Send the basket.
   *
   * `done` is passed in rather than read from state because SKIP places
   * immediately: the skip and the send happen in one click, and reading a
   * state value set by that same click would read the value before it.
   */
  const place = useCallback(
    async (done: boolean) => {
      if (!selected || !connection || !legs || !preview?.atmStrike) return

      /*
       * The deployment records ONE expiry and a basket may now span two, so
       * it records the NEAREST — the date the structure starts expiring.
       * Every leg also sends its own, which is what the server checks each
       * token against; this is the heading on the record, not a constraint.
       */
      const basketExpiry = legs
        .map((_, i) => expiryOf(i))
        .filter((e) => e !== '')
        .sort()[0]
      if (basketExpiry === undefined) return

      /*
       * The same refusal `placeOrder` makes for the ladder, and it has to be
       * repeated because a deployment does NOT go through it — it posts to
       * `/strategies/:id/deploy` directly. Without this the one path that
       * sends the most legs at once is the one that still learns the market is
       * shut from the server. Simulation is exempt for the same reason it is
       * there: a session runs at any hour and its feed is live by
       * construction.
       */
      if (
        tickSocket.getMarketContext().source !== 'SIMULATION' &&
        !isTradingWindow(new Date())
      ) {
        setError(
          'The market is closed. Orders can be placed between 09:15 and 15:40 IST.',
        )
        return
      }

      setPlacing(true)
      setError(null)
      try {
        await api.post(`/strategies/${selected.id}/deploy`, {
          simulationSessionId: tickSocket.getMarketContext().sessionId,
          connectionId: connection.id,
          underlying,
          expiry: basketExpiry,
          lots,
          clientRequestId: `dep-${selected.id}-${Date.now()}`,
          spotAtEntry: spot,
          atmStrike: preview.atmStrike,
          /*
           * Recorded, not enforced — the server accepts an entry that skipped
           * the checks. What matters afterwards is which entries were taken
           * with the stop and which without.
           */
          checklistDone: done,
          /*
           * The rules themselves, so a later edit to the strategy cannot
           * restate what was confirmed on this entry.
           */
          checklist,
          /*
           * The contracts the preview SHOWED. The trader approved these four,
           * and re-resolving server-side against a fuller chain could place
           * strikes they never saw.
           */
          legs: legs.map((l, i) => ({
            token: l.token,
            side: l.side,
            lots: l.lots,
            /*
             * 9.43. The expiry THIS leg trades, which is the basket's only
             * on a single-expiry structure. The token already identifies
             * the contract; this is sent so the server can check that the
             * two agree rather than trusting one of them.
             */
            expiry: expiryOf(i),
            ratio: selected.legs[i]?.ratio ?? 1,
            strikeCriteria: selected.legs[i]?.strikeCriteria,
            /*
             * 9.10. The role the TEMPLATE carries, so every firing of a
             * labelled strategy arrives labelled. Null when the trader has
             * not said, and stays null on the trade — never defaulted to MAIN.
             */
            role: selected.legs[i]?.role ?? null,
            /*
             * 7.5. Absent rather than null when the trader configured none,
             * so "no protection" and "not mentioned" stay the same thing all
             * the way to `openTrade`.
             */
            ...(edits.get(i)?.sl ? { sl: edits.get(i)!.sl } : {}),
            ...(edits.get(i)?.tgt ? { tgt: edits.get(i)!.tgt } : {}),
          })),
        })
        /*
         * Silent on success, as the ladder's B and S are. Accepted is not
         * filled, the drawer this opens is where the fills land, and a basket
         * said "accepted by the broker" even when a leg was about to be
         * rejected a moment later. `useOrderFailureToasts` speaks for the legs
         * that fail, naming each one.
         */
        onPlaced()
        onClose()
      } catch (e: unknown) {
        const why =
          e instanceof Error ? e.message : 'The strategy could not be placed.'
        setError(why)
        notifyFail(`${directedName(selected.name, intent)} not placed`, why)
      } finally {
        setPlacing(false)
      }
    },
    [
      selected,
      connection,
      expiryOf,
      legs,
      preview,
      underlying,
      spot,
      intent,
      onPlaced,
      onClose,
    ],
  )

  /**
   * Open the basket builder on these legs.
   *
   * It hands over the EDITED legs — `legs`, not `preview.legs` — because a
   * strike changed in this ticket is a strike the trader has already chosen,
   * and arriving in the builder to find it reverted would read as the edit
   * being lost.
   *
   * The ticket closes. Two surfaces describing the same basket, one of them
   * stale behind the other, is the state where a trader places from the
   * wrong one.
   */
  const adjust = useCallback(() => {
    if (!onAdjust || !selected || !legs || !(lotSize > 0)) return
    onAdjust({
      underlying,
      name: directedName(selected.name, intent),
      strategyId: strategy?.id ?? null,
      legs: legs.map((l, i) => ({
        token: l.token,
        side: l.side,
        lots: l.lots,
        strike: l.strike,
        optionType: l.optionType,
        expiry: expiryOf(i),
        tradingSymbol: `${underlying} ${l.strike} ${l.optionType}`,
        lotSize,
      })),
    })
    onClose()
  }, [
    onAdjust,
    selected,
    legs,
    lotSize,
    underlying,
    intent,
    strategy,
    expiryOf,
    onClose,
  ])

  if (!open || !selected) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className="qbar-card flex max-h-[86vh] w-full max-w-[620px] flex-col overflow-hidden"
        data-testid="strategy-picker"
      >
        {/*
          ── THE HEADER, IN TWO ROWS ───────────────────────────────────
          One row could not hold it. Six things were competing for 620px
          — a badge, a name, the instrument, two figures and a close
          button — so the name wrapped to three lines and "₹45.37 L" broke
          across two.

          So it is split by KIND, which is also how it is read: what is
          being traded on top, what it costs underneath. The numbers get
          a row of their own with room to be read, and neither row can
          squeeze the other.
        */}
        <header className="border-b border-border px-5 pb-3 pt-3.5">
          <div className="flex items-center gap-2.5">
            {/*
              The direction in words, not implied by which button was
              clicked a modal ago. It is the single fact most likely to be
              wrong in the trader's head at this point.
            */}
            <span
              className={cn(
                'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-extrabold tracking-wider',
                intent === 'SELL'
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-success/10 text-success',
              )}
              data-testid="picker-direction"
            >
              {intent === 'SELL' ? 'SELLING' : 'BUYING'}
            </span>

            {/*
              Editable, and what gets edited is the STORED name — the
              heading shows a Short Straddle bought as "Long Straddle", and
              starting the edit from that would rename the saved strategy
              to its own mirror image on one click of the pencil.
            */}
            <h2 className="min-w-0 truncate text-[15px] font-semibold">
              <StrategyName
                display={directedName(selected.name, intent)}
                stored={strategy?.name ?? selected.name}
                busy={placing}
                onRename={rename}
                testId="picker-title"
              />
            </h2>

            <span className="flex-1" />

            <span className="num flex shrink-0 items-center gap-1.5 text-[11.5px] text-muted-foreground">
              {/*
                NO EXPIRY HERE — 9.43. It was one control for the whole
                ticket, which is the wrong shape: a leg owns its expiry
                now, the selector is on every row of the table below, and
                a header that named one date would contradict a basket
                spanning two.
              */}
              {underlying}
              <span aria-hidden>·</span>
              {spot > 0 ? spot.toFixed(2) : '—'}
            </span>

            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="-mr-1 shrink-0 rounded p-1 text-muted-foreground hover:bg-muted"
            >
              <X size={16} />
            </button>
          </div>

          {/*
            The two figures that decide the trade, side by side and the
            same size, because they answer one question together: what it
            collects, and what it ties up.

            MARGIN FIRST, reading order being left to right and the money
            blocked being the constraint a desk hits before the credit
            matters.
          */}
          <div className="mt-2.5 flex items-stretch gap-2">
            <div
              className="flex min-w-0 flex-1 flex-col gap-0.5 rounded-lg border border-border bg-muted/30 px-3 py-2"
              data-testid="picker-margin"
            >
              <span className="text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                Margin required ≈
              </span>
              {margin?.ok ? (
                <span
                  className="num truncate text-[16px] font-semibold"
                  title={marginTitle(margin)}
                >
                  ₹{compactInr(margin.margin.total)}
                  {lots > 1 && (
                    <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">
                      {lots} lots
                    </span>
                  )}
                </span>
              ) : (
                <span
                  className="truncate text-[12px] text-warning"
                  title={margin?.why}
                >
                  {margin?.why ?? '—'}
                </span>
              )}
            </div>

            <div
              className="flex min-w-0 flex-1 flex-col gap-0.5 rounded-lg border border-border bg-muted/30 px-3 py-2"
              data-testid="picker-premium-top"
            >
              <span className="text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                Net premium
              </span>
              {netPremium === null ? (
                <span
                  className="text-[12px] text-warning"
                  data-testid="preview-unpriced"
                >
                  not priceable
                </span>
              ) : (
                <span
                  className={cn(
                    'num truncate text-[16px] font-semibold',
                    netPremium >= 0 ? 'text-success' : 'text-destructive',
                  )}
                  data-testid="preview-premium"
                >
                  {netPremium >= 0 ? 'credit ' : 'debit '}₹
                  {Math.abs(Math.round(netPremium)).toLocaleString('en-IN')}
                </span>
              )}
            </div>
          </div>
        </header>

        <div
          className="flex-1 overflow-y-auto p-5"
          data-testid="picker-preview"
        >
          {/*
            NO CHIP ROW. It repeated the leg table three inches below it,
            and the table is where the legs can now be CHANGED — two
            spellings of the same thing, one of them stale the moment an
            edit lands.
          */}
          <PreviewBody
            preview={preview}
            legs={legs}
            editProblem={editProblem}
            expiries={expiries ?? []}
            rowsFor={rowsFor}
            expiryOf={expiryOf}
            lotSize={lotSize}
            spot={spot}
            underlying={underlying}
            atmStrike={preview?.atmStrike ?? null}
            onEdit={(index, edit) =>
              setEdits((prev) => {
                const next = new Map(prev)
                next.set(index, edit)
                return next
              })
            }
            /*
             * Read back out of the same map the edits live in. The editor
             * rewrites a whole `LegEdit` on every change, so it has to be
             * told what the other fields currently are — otherwise changing
             * a strike would silently clear the stop beside it.
             */
            protectionOf={(index) => ({
              sl: edits.get(index)?.sl ?? null,
              tgt: edits.get(index)?.tgt ?? null,
            })}
          />

          {/*
            AFTER the legs and the premium. The checks are about whether to
            take a trade that has already been described — asking them above
            the numbers asks them about nothing.
          */}
          {strategy && (
            <PreTradeChecklist
              strategy={strategy}
              resetKey={`${String(open)}:${strategy.id}:${intent}`}
              onReadyChange={onChecklist}
              onSkip={() => void place(false)}
              {...(onStrategyChanged ? { onStrategyChanged } : {})}
            />
          )}
        </div>

        <footer className="flex items-center gap-3 border-t border-border px-5 py-3">
          {error && (
            <span
              className="flex-1 text-[12px] text-destructive"
              data-testid="picker-error"
            >
              {error}
            </span>
          )}
          {!error && sizingProblem && (
            <span
              className="flex-1 text-[12px] text-warning"
              data-testid="picker-sizing-problem"
            >
              {sizingProblem}
            </span>
          )}
          {!error && !sizingProblem && (
            <span
              className="flex-1 text-[11.5px] text-muted-foreground"
              data-testid="picker-size"
            >
              {lots} lot{lots === 1 ? '' : 's'} · {lots * lotSize} qty per
              ratio-1 leg
            </span>
          )}

          {/*
            ADJUSTMENTS, beside Cancel rather than beside Place — 9.69.
            It does not send, and the two buttons on the right of a ticket
            are the two that decide an order. A trader reaching for "place"
            must never find "open another screen" under their hand.
          */}
          {onAdjust && (
            <Button
              type="button"
              variant="outline"
              onClick={adjust}
              disabled={!legs?.length || editProblem !== null}
              data-testid="picker-adjust"
              title="Edit these legs on the ladder, add or remove any, then place"
            >
              <SlidersHorizontal size={14} />
              Add Leg
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void place(checklistDone)}
            disabled={!ready || placing}
            data-testid="picker-place"
          >
            {placing
              ? 'Placing…'
              : `${intent === 'SELL' ? 'Sell' : 'Buy'} ${structureLabel(selected.name)}`}
          </Button>
        </footer>
      </div>
    </div>
  )
}

/** The breakdown, for the title attribute — where the number came from. */
function marginTitle(estimate: MarginEstimate): string {
  if (!estimate.ok) return estimate.why
  const m = estimate.margin
  if (m.scanRisk === 0 && m.exposure === 0)
    return 'Premium payable — a long basket blocks no margin.'
  return [
    `SPAN scan ₹${compactInr(m.scanRisk)} (price scan ±${(m.priceScanRange * 100).toFixed(1)}%)`,
    `exposure ₹${compactInr(m.exposure)}`,
    m.expiryElm > 0 ? `expiry-day ELM ₹${compactInr(m.expiryElm)}` : null,
    'Estimated by the exchange’s method — this broker serves no margin endpoint.',
  ]
    .filter(Boolean)
    .join(' · ')
}

function PreviewBody({
  preview,
  legs,
  editProblem,
  expiries,
  rowsFor,
  expiryOf,
  lotSize,
  spot,
  underlying,
  atmStrike,
  onEdit,
  protectionOf,
}: {
  preview: StrategyPreview | undefined
  /** The legs AFTER this placement's edits — what will actually be sent. */
  legs: readonly ResolvedLeg[] | null
  /** Why an edited leg could not be pointed at a contract, when it could not. */
  editProblem: string | null
  expiries: readonly string[]
  rowsFor: (expiry: string) => readonly PreviewChainRow[]
  expiryOf: (index: number) => string
  lotSize: number
  spot: number
  underlying: string
  atmStrike: number | null
  onEdit: (index: number, edit: LegEdit) => void
  protectionOf: (index: number) => {
    sl: ProtectionSpec | null
    tgt: ProtectionSpec | null
  }
}) {
  const payoffLegs = useMemo(
    () => (legs ? payoffLegsOf(legs, lotSize) : null),
    [legs, lotSize],
  )

  return (
    <>
      {!legs && (
        /*
         * Resolution failed. It says WHY — an unlisted strike, a premium that
         * matched nothing — rather than showing an empty table, because the
         * reason is usually actionable (widen the chain, pick another expiry).
         */
        <div
          className="rounded-[10px] border p-3 text-[12.5px]"
          style={{
            borderColor: 'var(--tr-warn-line)',
            background: 'var(--tr-warn-bg)',
          }}
          data-testid="preview-unresolved"
        >
          <div>{editProblem ?? preview?.why ?? 'Waiting for prices…'}</div>
          {/*
            THE CAUSES, NOT JUST THE COUNT. "6 of 6 leg(s) could not be
            resolved" names a number and no action; an unlisted strike and
            an expiry that is not loaded are different problems with
            different fixes, and the trader can only act on the one they
            are shown. Distinct reasons, because six legs failing for the
            same reason is one sentence, not six.
          */}
          {editProblem === null &&
            distinctWhy(preview?.unresolved).map((why) => (
              <div key={why} className="mt-1 pl-3 text-[11.5px] opacity-80">
                — {why}
              </div>
            ))}
        </div>
      )}

      {legs && (
        <>
          {/*
            THE SHAPE, BEFORE THE TABLE. A trader reads four rows of
            SELL/BUY and strikes slowly; they read the curve at a glance,
            and the curve is what says where this position stops making
            money. Drawn at the prices the order will cross at — see
            `previewPayoff.ts`.
          */}
          {payoffLegs ? (
            <>
              {/*
                `compact` hides the chart's own statistics line. Four
                correct numbers — max profit, max loss, two breakevens —
                left the reader to assemble the trade in their head: which
                side of a breakeven is the good side, whether the loss is
                above or below, whether the profit is capped. The two
                sentences underneath say it instead.
              */}
              <PayoffChart
                legs={payoffLegs}
                spot={spot > 0 ? spot : null}
                height={168}
                compact
                className="mb-2"
              />
              <PayoffExplain
                legs={payoffLegs}
                symbol={underlying}
                spot={spot > 0 ? spot : null}
                className="mb-4"
              />
            </>
          ) : (
            <p
              className="mb-4 text-[11.5px] text-muted-foreground"
              data-testid="preview-no-payoff"
            >
              No payoff curve — one of these legs has no price, and a curve
              drawn through a missing price would put the breakevens somewhere
              the trade cannot reach.
            </p>
          )}

          {/*
            THE LEGS, EDITABLE. The strike comes from a picker of what this
            expiry actually lists — a typed strike can be one the exchange
            does not have, and the resolver would refuse it at the worst
            moment — and the size is per leg, because a template's ratio is
            a starting point rather than a verdict.

            The EXPIRY is on every row and is a CONTROL — 9.43. A calendar
            spread is the same strike on two dates, so one expiry for the
            whole ticket could not express the thing the builder was
            already letting traders save.
          */}
          <LegEditor
            legs={legs}
            expiries={expiries ?? []}
            rowsFor={rowsFor}
            expiryOf={expiryOf}
            lotSize={lotSize}
            atmStrike={atmStrike}
            onEdit={onEdit}
            protectionOf={protectionOf}
            priceOf={(token) => tickStore.snapshot(token)?.ltp ?? 0}
          />

          {/*
            NOTHING ELSE. The net premium moved to the header, and the three
            paragraphs that used to sit here — how the book prices a basket,
            why no margin is shown, the lot arithmetic — were explanation
            rather than information, read once and then scrolled past every
            time. The margin BEHAVIOUR is unchanged: [B9] this gateway
            serves no margin endpoint and we still do not estimate one
            (ROADMAP 5.6), we simply no longer say so on every placement.
          */}
        </>
      )}
    </>
  )
}
